'use client';

import { useStore } from '@/providers/StoreProvider';
import { addNewReport, type Building } from '@repo/api';
import { useEffect, useMemo, useState } from 'react';
import Ratings from '../report/Ratings';
import TenancyLength from '../report/TenancyLength';
import RentIncrease from '../report/RentIncrease';
import RentValue from '../report/RentValue';
import ViolationHistory from '../report/ViolationHistory';
import ComplaintHistory from '../report/ComplaintHistory';
import BedbugInfestation from '../report/BedbugInfestation';
import RodentInfestation from '../report/RodentInfestation';
import Construction from '../report/Construction';
import { ReportActionBar } from '../header/ReportActionBar';
import { BuildingInfoCard } from '../info/BuildingInfoCard';
interface ReportDetailContentProps {
  building: Building;
  params: {
    city: string;
    buildingId: string;
    slug: string;
  };
  isActive: boolean;
}

function ReportDetailContent({ building, isActive }: ReportDetailContentProps) {
  const { data: explorerData, loading: explorerLoading } = useStore(state => state.explorer);
  const fetchExplorerData = useStore(state => state.explorer.fetchExplorerData);

  const [showSkeleton, setShowSkeleton] = useState(true);
  const [isReportReady, setIsReportReady] = useState(false);
  // Run-once key per building (so changing building works)
  const initKey = useMemo(() => `report-init:${building.slug}`, [building.slug]);

  async function addReportAndRefresh() {
    await addNewReport({ buildingSlug: building.slug });
    await fetchExplorerData();
  }

  useEffect(() => {
    if (!isActive) return;
    // While explorer is loading, keep skeleton
    if (explorerLoading) return;

    // Explorer finished loading — we should stop the skeleton no matter what
    // (unless we choose to keep it while addReport is running)
    const stopSkeleton = () => setTimeout(() => setShowSkeleton(false), 300);

    // If explorerData/accessType is missing even after loading,
    // render dummy/locked UI (no API calls)
    if (!explorerData?.accessType) {
      setIsReportReady(false);
      stopSkeleton();
      return;
    }

    const accessBuildingsSlugs =
      explorerData.accessBuildingsData?.map((b: any) => b.slug).filter(Boolean) || [];
    const freeAccessSlugs =
      explorerData.freeAccessBuildings?.map((b: any) => b.slug).filter(Boolean) || [];

    const hasAccessNow =
      accessBuildingsSlugs.includes(building.slug) || freeAccessSlugs.includes(building.slug);

    // If user already has access, mark ready and stop skeleton
    if (hasAccessNow) {
      setIsReportReady(true);
      stopSkeleton();
      return;
    }

    (async () => {
      try {
        await addReportAndRefresh();
        setIsReportReady(true);
      } catch {
        // addReport failed or user not eligible → show dummy/locked UI
        setIsReportReady(false);
      } finally {
        stopSkeleton();
      }
    })();
  }, [isActive, explorerLoading, initKey, building.slug, explorerData, fetchExplorerData]);

  // Access is unknown while the skeleton is up, so hold both unlock CTAs back
  // until the explorer/addReport flow settles.
  const isLocked = !showSkeleton && !isReportReady;

  return (
    <>
      <div className="dynamic-grid-container !pt-8 !pb-24">
        <div className="content-grid">
          <div className="col-span-full lg:col-span-7 space-y-8">
            <ViolationHistory
              building={building}
              buildingAddress={building.primaryAddress?.streetAddress ?? ''}
              showSkeleton={showSkeleton}
              isReportReady={isReportReady}
            />
            <ComplaintHistory
              building={building}
              buildingAddress={building.primaryAddress?.streetAddress ?? ''}
              showSkeleton={showSkeleton}
              isReportReady={isReportReady}
            />
            <div id="bedbug-infestation">
              <BedbugInfestation
                building={building}
                buildingAddress={building.primaryAddress?.streetAddress ?? ''}
                showSkeleton={showSkeleton}
                isReportReady={isReportReady}
              />
            </div>
            <RodentInfestation
              building={building}
              buildingAddress={building.primaryAddress?.streetAddress ?? ''}
              showSkeleton={showSkeleton}
              isReportReady={isReportReady}
            />
            <Ratings
              building={building}
              buildingAddress={building.primaryAddress?.streetAddress ?? ''}
              showSkeleton={showSkeleton}
              isReportReady={isReportReady}
            />
            <TenancyLength
              building={building}
              buildingAddress={building.primaryAddress?.streetAddress ?? ''}
              showSkeleton={showSkeleton}
              isReportReady={isReportReady}
            />
            <RentIncrease
              building={building}
              buildingAddress={building.primaryAddress?.streetAddress ?? ''}
              showSkeleton={showSkeleton}
              isReportReady={isReportReady}
            />
            <RentValue
              building={building}
              buildingAddress={building.primaryAddress?.streetAddress ?? ''}
              showSkeleton={showSkeleton}
              isReportReady={isReportReady}
            />
            <Construction
              building={building}
              buildingAddress={building.primaryAddress?.streetAddress ?? ''}
              showSkeleton={showSkeleton}
              isReportReady={isReportReady}
            />
          </div>

          <div className="hidden lg:block sticky top-8 self-start lg:col-start-9 lg:col-span-4 space-y-6 w-full pr-4">
            <BuildingInfoCard building={building} />
            {!isReportReady ? (
              <div className="">
                <ReportActionBar className="!shadow rounded-3xl border border-tertiary" />
              </div>
            ) : null}
          </div>
        </div>
      </div>
      {isLocked ? (
        <div className="md:hidden fixed bottom-0 left-0 w-dvw right-0 z-12">
          <ReportActionBar />
        </div>
      ) : null}
    </>
  );
}

export default ReportDetailContent;
